import type { ListBuilderConfiguration } from '../types';

/**
 * Payment Schedules list configuration for the client detail embedded list.
 * Columns: Name, Type, Status, Frequency (+ Next Run), Payment Method, Actions
 */
export const PAYMENT_SCHEDULES_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-payment-schedules',
			cacheTTL: 300000,
			name: 'Payment Schedules',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'schedule-name',
					headers: [
						{
							id: 'schedule-name-header',
							text: 'Name',
						},
					],
					fields: [
						{
							id: 'schedule-name-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'name',
									objectTable: 'payment_schedule',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
				},
				{
					id: 'schedule-type',
					headers: [
						{
							id: 'schedule-type-header',
							text: 'Type',
						},
					],
					fields: [
						{
							id: 'schedule-type-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'type',
									objectTable: 'payment_schedule',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
				},
				{
					id: 'schedule-status',
					headers: [
						{
							id: 'schedule-status-header',
							text: 'Status',
						},
					],
					fields: [
						{
							id: 'schedule-status-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'status',
									objectTable: 'payment_schedule',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'value_map',
									valueMap: {
										active: 'success',
										paused: 'warning',
										completed: 'info',
										cancelled: 'default',
										failed: 'danger',
									},
								},
							},
							displayStyle: 'badge',
						},
					],
					sortable: true,
				},
				{
					id: 'schedule-frequency',
					headers: [
						{
							id: 'schedule-frequency-header',
							text: 'Frequency',
						},
					],
					fields: [
						{
							id: 'schedule-frequency-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'frequencyDisplay',
									objectTable: 'payment_schedule',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'frequency-line-break',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'next-run-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'nextRunDate',
									objectTable: 'payment_schedule',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'date',
						},
					],
					sortable: true,
				},
				{
					id: 'payment-method',
					headers: [
						{
							id: 'payment-method-header',
							text: 'Payment Method',
						},
					],
					fields: [
						{
							id: 'payment-method-display-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'paymentMethodDisplay',
									objectTable: 'payment_schedule',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
				},
				{
					id: 'actions',
					headers: [
						{
							id: 'actions-header',
							text: '',
						},
					],
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'pen-to-square',
									callbackName: 'editSchedule',
									variant: 'primary',
									visibility: 'visible',
									requiredPermission: 'transactions:payment_schedules:update',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'deleteSchedule',
									variant: 'danger',
									visibility: 'visible',
									requiredPermission: 'transactions:payment_schedules:delete',
								},
							],
						},
					],
					width: 80,
					align: 'center',
					sortable: false,
				},
			],
			defaultSortId: 'schedule-name-asc',
			sortOrder: 0,
		},
	],
	// Root-level tableSorts (TableSortOption name/fields) so defaultSortId
	// resolves — a list-level `sortFields` is not read by the resolver (PD-14536).
	tableSorts: [
		{
			id: 'schedule-name-asc',
			name: 'Name A → Z',
			fields: [
				{
					objectTable: 'payment_schedule',
					fieldName: 'name',
					direction: 'ASC',
				},
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/clients/:clientId/payment-schedules/:id',
			objectLabel: 'Payment Schedule',
			objectTable: 'payment_schedule',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'type', fieldLabel: 'Type' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'frequencyDisplay', fieldLabel: 'Frequency' },
				{ fieldName: 'paymentMethodDisplay', fieldLabel: 'Payment Method' },
				{ fieldName: 'nextRunDate', fieldLabel: 'Next Run Date' },
				{ fieldName: 'createdDate', fieldLabel: 'Created Date' },
			],
		},
	],
};
